import { useState } from 'react';
import { MapPin, Crown, Gem, Shield, Truck, Facebook, Instagram, Phone, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Separator } from './ui/separator';
import { LegalModals, LegalType } from './LegalModals';

interface FooterProps {
  onContactClick?: () => void;
}

export function Footer({ onContactClick }: FooterProps) {
  const [legalType, setLegalType] = useState<LegalType>(null);

  return (
    <footer className="bg-white border-t border-border mt-12">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-1 group">
                <div className="relative flex items-center">
                    <img
                        src="/rpk-logo.png"
                        alt="RPK Logo"
                        className="h-12 sm:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                    />
                </div>
                <div className="flex flex-col justify-center border-l border-brand-green/30 pl-2 py-1">
                    <span className="text-sm sm:text-base font-light tracking-[0.1em] text-brand-green leading-tight uppercase font-serif">
                        Ram Pratap Katta
                    </span>
                    <span className="text-[10px] sm:text-xs font-light tracking-[0.25em] text-brand-green leading-none uppercase font-serif mt-1">
                        & Sons Saraf
                    </span>
                </div>
            </div>
            <p className="text-muted-foreground text-sm">
              Premium Gold and Silver jewellery crafted with tradition and elegance. Your trusted partner for precious metals and timeless designs.
            </p>
            <div className="flex space-x-3">
              <a 
                href="https://www.facebook.com/rampratapkatta/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-8 w-8 rounded-full bg-brand-green/10 flex items-center justify-center hover:bg-brand-green/20 cursor-pointer transition-colors"
              >
                <Facebook className="h-4 w-4 text-brand-green" />
              </a>
              <a 
                href="https://www.instagram.com/rpksonssaraf/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-8 w-8 rounded-full bg-brand-green/10 flex items-center justify-center hover:bg-brand-green/20 cursor-pointer transition-colors"
              >
                <Instagram className="h-4 w-4 text-brand-green" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-brand-green">Quick Links</h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-brand-green transition-colors">About Us</Link></li>
              <li><Link to="/certification" className="text-muted-foreground hover:text-brand-green transition-colors">Certification</Link></li>
              <li><Link to="/size-guide" className="text-muted-foreground hover:text-brand-green transition-colors">Size Guide</Link></li>
              <li><Link to="/care-instructions" className="text-muted-foreground hover:text-brand-green transition-colors">Care Instructions</Link></li>
            </ul>
          </div>

          {/* Customer Service */}
          <div className="space-y-4">
            <h3 className="text-brand-green">Customer Service</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <button 
                  onClick={onContactClick} 
                  className="text-muted-foreground hover:text-brand-green transition-colors text-left"
                >
                  Contact Us
                </button>
              </li>
              <li><Link to="/faq" className="text-muted-foreground hover:text-brand-green transition-colors">FAQ</Link></li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-brand-green">Contact Info</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-brand-green" />
                <span className="text-muted-foreground">+91-9829050157</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4 text-brand-green" />
                <span className="text-muted-foreground">info@rpksons.com</span>
              </div>
              <div className="flex items-start space-x-2">
                <MapPin className="h-4 w-4 text-brand-green mt-0.5" />
                <span className="text-muted-foreground">
                  Shop no 148, Jai Complex, <br />
                  Arya Nagar <br />Alwar <br /> Rajasthan 301001
                </span>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Features Section */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="flex items-center space-x-3">
            <Shield className="h-5 w-5 text-brand-green" />
            <div>
              <p className="text-sm font-medium">Certified Quality</p>
              <p className="text-xs text-muted-foreground">BIS Hallmarked</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Gem className="h-5 w-5 text-brand-green" />
            <div>
              <p className="text-sm font-medium">Premium Quality</p>
              <p className="text-xs text-muted-foreground">Finest craftsmanship</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Crown className="h-5 w-5 text-brand-green" />
            <div>
              <p className="text-sm font-medium">Lifetime Support</p>
              <p className="text-xs text-muted-foreground">Customer satisfaction</p>
            </div>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground">
          <p>Ram Pratap Katta & Sons Saraff. <br />All rights reserved &copy; 2026</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <button 
              onClick={() => setLegalType('privacy')}
              className="hover:text-brand-green transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => setLegalType('terms')}
              className="hover:text-brand-green transition-colors"
            >
              Terms of Service
            </button>
            <button 
              onClick={() => setLegalType('cookies')}
              className="hover:text-brand-green transition-colors"
            >
              Cookies
            </button>
          </div>
        </div>
      </div>

      <LegalModals 
        type={legalType} 
        isOpen={!!legalType} 
        onClose={() => setLegalType(null)} 
      />
    </footer>
  );
}